import { Toolbar } from '@ariakit/react/toolbar';
import type { BehaviorSubject, Observable } from 'rxjs';
import type { GraphType } from '@/graphql/types';
import type { CursorFunction } from '../../../graph/cursor-functions';
import { CursorFunctionSelector } from './CursorFunctionSelector';
import { GraphToolbarGroup } from './GraphToolbarGroup';
import { GraphTypeSelector, type GraphTypeController } from './GraphTypeSelector';
import { GraphZoomButtons, type GraphZoomController } from './GraphZoomButtons';

interface ToolbarGraph extends GraphZoomController {
    graphType$?: Observable<GraphType>;
    setGraphType?: (graphType: GraphType) => void;
    cursorFunction$?: BehaviorSubject<CursorFunction>;
}

interface Props {
    graph: ToolbarGraph;
    showCursorFunctions?: boolean;
    showGraphType?: boolean;
}

function hasGraphTypeControls(graph: ToolbarGraph): graph is ToolbarGraph & GraphTypeController {
    return !!graph.graphType$ && !!graph.setGraphType;
}

export function GraphToolbar({
    graph,
    showCursorFunctions = false,
    showGraphType = false,
}: Props): JSX.Element {
    return (
        <Toolbar className="inline-flex items-center gap-2">
            {showCursorFunctions && graph.cursorFunction$ && (
                <GraphToolbarGroup>
                    <CursorFunctionSelector cursorFunction$={graph.cursorFunction$} />
                </GraphToolbarGroup>
            )}

            {/* Walkthrough compatibility: keep non-Tailwind class `graph-display-types`;
                it is used as selector in assets/js/walkthrough/schema.ts. */}
            {showGraphType && hasGraphTypeControls(graph) && (
                <GraphToolbarGroup className="graph-display-types">
                    <GraphTypeSelector graph={graph} />
                </GraphToolbarGroup>
            )}

            {/* Walkthrough compatibility: keep non-Tailwind class `graph-zoom-types`;
                it is used as selector in assets/js/walkthrough/schema.ts. */}
            <GraphToolbarGroup className="graph-zoom-types">
                <GraphZoomButtons graph={graph} />
            </GraphToolbarGroup>
        </Toolbar>
    );
}
